import { z } from 'zod';

import type { EvalResult } from '../../env.ts';
import { type AstInput, nodePos } from '../../normalize.ts';
import { type ColumnRefInArg, opMeta, type SpecialCtx } from '../../op-meta.ts';
import { kwargPath, paramPath } from '../../path.ts';
import { materializedToRows } from '../../runtime/arquero.ts';
import { cellKey, displayCell } from '../../runtime/cell-key.ts';
import { groupRowsBy } from '../../runtime/partition.ts';
import { astArg } from '../../schema/ast-codec.ts';
import { type ColumnNameAst, ColumnNameAstSchema, columnNameAstString } from '../../schema/keyword.ts';
import { type ColumnType, type Schema, schemaFromEntries, unknownCol } from '../../schema/types.ts';
import { type Cell, MaterializedDataset, synthesizeIdentity } from '../../value.ts';
import { aggContextScope, aggInputColumn, aggResultType, compileAggCallReducer } from './agg-funcs.ts';
import { requireColumns, resolveDsArg, rowsToColumns, vectorColRefs } from './helpers.ts';

/**
 * `(pivot ds :rows [:r…] :cols [:c…] :values (<agg> :col))` — long →
 * wide reshape. Distinct value combinations of the `:cols` columns
 * become new output columns; the `:values` agg-call reduces the row-set
 * inside each (row × col) bin to a single cell. The `:rows` columns
 * become the output's id columns and survive verbatim (one output row
 * per distinct id-combination). Input columns not named in
 * `:rows`/`:cols` and not referenced by `:values` are silently dropped.
 *
 * Multi-key cols: `:cols [:a :b]` produces compound column names by
 * joining each row's values with `_` (e.g. `Q1_base`, `Q1_high`).
 *
 * The agg-call sees the FULL original row of every bin member, so
 * keyword col-refs in the agg (e.g. `(sum :revenue)`) resolve against
 * the input dataset's columns — symmetric with `aggregate` /
 * `time-bucket`.
 *
 * Errors:
 *
 *   - First arg not a `Dataset` → `TypeMismatchError`.
 *   - A `:rows` or `:cols` name isn't a column on `ds.schema` →
 *     `UnknownColumnRefError`.
 *   - `:values` names an op that isn't an agg func → `UnknownOpError`.
 *
 * A `LazyDataset` input is forced via `ctx.materialize` before the
 * reshape runs (pivot's output schema depends on RUNTIME values of the
 * `:cols` columns, so a precise pre-eval schema can't be declared).
 */
export const pivot = z
  .object({
    name: z.literal('pivot'),
    params: z.tuple([astArg('<dataset>')]),
    rows: z.array(ColumnNameAstSchema).min(1),
    cols: z.array(ColumnNameAstSchema).min(1),
    values: z.object({
      name: z.string().min(1),
      params: z.array(astArg()).optional(),
    }),
  })
  .describe(
    'Long → wide reshape. `:rows` = id columns; `:cols` = pivot key columns (compound when multi); `:values` = exactly ONE agg-call reducing each (row × col) bin — `:values (mean :v)`, never `((mean :v))` (extra parens) and never a vector `[(mean :v)]`. Output column names come from the `:cols` values and cannot contain spaces — `derive` a space-free key first when needed.',
  )
  .register(opMeta, {
    // Special evaluation: the `:values` slot holds an agg-call AST
    // whose op (`sum`, `mean`, …) lives in `aggMeta`, NOT `opMeta` —
    // eager dispatch would `evalNode` it and surface an UnknownOpError
    // before apply runs. The dataset positional is evaluated by hand
    // via `ctx.evalNode`; `:rows` / `:cols` arrive as keyword/string
    // AST shapes (extracted with `columnNameAstString`).
    evaluation: 'special',
    effect: 'pure',
    childScopes: [aggContextScope],
    example: { expr: '(-> sales (pivot :rows [:region] :cols [:quarter] :values (sum :revenue)))' },
    apply: async (data, ctx: SpecialCtx): Promise<EvalResult> => {
      const dsAst = (data.params as readonly [AstInput])[0];
      const { value: dsValue } = await ctx.evalNode(dsAst, ctx.env, paramPath(ctx.path, 0));
      const concrete = await resolveDsArg(dsValue, '(pivot ds :rows […] :cols […] :values <agg>)', ctx);

      const rowCols = (data.rows as readonly ColumnNameAst[]).map(columnNameAstString);
      const colCols = (data.cols as readonly ColumnNameAst[]).map(columnNameAstString);

      requireColumns(rowCols, concrete.schema, (idx) =>
        nodePos(ctx.spans, paramPath(kwargPath(ctx.path, 'rows'), idx)),
      );
      requireColumns(colCols, concrete.schema, (idx) =>
        nodePos(ctx.spans, paramPath(kwargPath(ctx.path, 'cols'), idx)),
      );

      const aggReducer = await compileAggCallReducer(data.values, kwargPath(ctx.path, 'values'), ctx.spans);
      const rows = materializedToRows(concrete);

      // Two-level grouping via the shared `groupRowsBy` SSOT: first
      // by id cols (one output row per group), then by bin cols
      // inside each id group (one output cell per (id × bin)).
      //
      // Each bin carries TWO derived strings:
      //   - `binId` (composite `cellKey`) — structural identity,
      //     used as the map / set key so struct cells dedupe by
      //     content across id-groups.
      //   - `binName` (composite `displayCell`) — human-readable,
      //     becomes the output column name. Joined with `_` (the
      //     established compound-bin convention).
      const idGroups = groupRowsBy(rows, rowCols).map((idg) => ({
        idVals: Object.fromEntries(rowCols.map((c) => [c, idg.representativeRow[c]])),
        binGroups: groupRowsBy(idg.rows, colCols).map((bg) => ({
          binId: colCols.map((c) => cellKey(bg.representativeRow[c] ?? null)).join('|'),
          binName: colCols.map((c) => displayCell(bg.representativeRow[c] ?? null)).join('_'),
          binRows: bg.rows,
        })),
      }));

      // Distinct bins across all id-groups, keyed by binId. binName
      // is invariant per binId (deterministic from the same cells).
      //
      // Output column names must be unique. `displayCell` is NOT
      // injective on the cell universe (a number `1` and a string
      // `"1"` both render as `"1"`); a column with mixed cell types
      // can therefore produce two distinct `binId`s that share a
      // `binName`. When that happens, fall back to `binId` for the
      // colliding bins so distinct buckets keep distinct column names
      // and no data is silently merged.
      const allBins = new Map(idGroups.flatMap((g) => g.binGroups.map((b) => [b.binId, b.binName] as const)));
      const nameUseCounts = Array.from(allBins.values()).reduce<Map<string, number>>(
        (acc, name) => acc.set(name, (acc.get(name) ?? 0) + 1),
        new Map(),
      );
      const finalNameById = new Map(
        Array.from(allBins, ([id, name]) => [id, (nameUseCounts.get(name) ?? 0) > 1 ? id : name] as const),
      );
      const sortedBins = Array.from(finalNameById.entries()).sort(([, a], [, b]) => a.localeCompare(b));
      const sortedBinNames = sortedBins.map(([, name]) => name);

      const outRows = idGroups.map(({ idVals, binGroups }) => {
        const binMap = new Map(binGroups.map((b) => [b.binId, b.binRows]));
        return {
          ...(idVals as Record<string, Cell>),
          ...Object.fromEntries(sortedBins.map(([id, name]) => [name, aggReducer(binMap.get(id) ?? []) as Cell])),
        };
      });

      const outCols = rowsToColumns(outRows, [...rowCols, ...sortedBinNames]);
      // SYNTHESIS: one output row per distinct `:rows` combination. It
      // keeps the group's single instance identity IFF `:rows` includes
      // both `space` and `externalId` (the id columns survive verbatim);
      // else undefined.
      return {
        value: new MaterializedDataset(
          outCols,
          propagatePivotSchema(concrete.schema, rowCols, sortedBinNames, data.values),
          synthesizeIdentity(outCols, rowCols, concrete.schema),
        ),
        env: ctx.env,
      };
    },
    dataset: {
      // Runtime-width: pivoted column names come from distinct runtime
      // values of the `:cols` columns. Declaring `dataset` engages the
      // engine's three-branch wrapper; returning `undefined` triggers
      // force-materialise so apply computes the precise schema.
      propagateSchema: () => undefined,
      // `:rows` and `:cols` are row-accessor USAGES (id keys and pivot
      // keys). The `:values` agg-call's input column is also a USAGE.
      // `:values`-named output columns are runtime-derived, not refs.
      referencedColumns: (data, basePath): readonly ColumnRefInArg[] => {
        const kwargs = data as { readonly rows?: unknown; readonly cols?: unknown; readonly values?: unknown };
        const rowRefs = vectorColRefs(kwargs.rows, kwargPath(basePath, 'rows'));
        const colRefs = vectorColRefs(kwargs.cols, kwargPath(basePath, 'cols'));
        const valuesAgg = kwargs.values as { readonly params?: readonly unknown[] } | undefined;
        const valueColRefs =
          valuesAgg === undefined
            ? []
            : ((): readonly ColumnRefInArg[] => {
                const name = aggInputColumn(valuesAgg);
                return name === undefined ? [] : [{ name, path: `${kwargPath(basePath, 'values')}/params/0` }];
              })();
        return [...rowRefs, ...colRefs, ...valueColRefs];
      },
    },
  });

/**
 * Build the post-pivot schema. Id columns keep their input types; the
 * bin columns share the agg's result type (uniform per agg). `:rows` /
 * `:cols` names have already been validated against the input schema
 * by apply, so the lookups here are total.
 */
const propagatePivotSchema = (
  inputSchema: Schema,
  rowCols: readonly string[],
  binKeys: readonly string[],
  aggCall: { readonly name: string; readonly params?: readonly unknown[] },
): Schema => {
  const idEntries = rowCols.map((c) => [c, inputSchema.get(c) ?? unknownCol] as readonly [string, ColumnType]);
  const valueType = aggResultType(aggCall, inputSchema);
  const binEntries = binKeys.map((k) => [k, valueType] as readonly [string, ColumnType]);
  return schemaFromEntries([...idEntries, ...binEntries]);
};
